import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import PageHeader from '../common/PageHeader';
import Reveal from '../common/Reveal';
import { SITE } from '../../config/site';
import { useLang, usePageTitle } from '../../i18n/LanguageContext';

// `website` is a hidden honeypot field: real visitors never see or fill it, bots usually do.
const EMPTY = { full_Name: '', email: '', phone: '', company: '', service: '', message: '', website: '' };

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contact;
  usePageTitle(c.pageTitle);

  const [searchParams] = useSearchParams();
  const topicKeys = c.form.topics.map((tp) => tp.key);
  const initialTopic = topicKeys.includes(searchParams.get('topic')) ? searchParams.get('topic') : '';

  const [form, setForm] = useState({ ...EMPTY, service: initialTopic });
  const [sending, setSending] = useState(false);

  // Pre-select the topic when arriving from a CTA such as /contact?topic=partnership
  useEffect(() => {
    const q = searchParams.get('topic');
    if (q && topicKeys.includes(q)) setForm((f) => ({ ...f, service: q }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_Name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(c.form.required);
      return;
    }
    setSending(true);
    try {
      // Same payload fields the mail service already expects (full_Name, email, phone, service, message).
      // The topic is sent as an English label so inquiries are easy to triage; the company is added to the message.
      const topic = form.service || 'general';
      const topicLabel = `${topic} — ${(c.form.topics.find((tp) => tp.key === topic) || {}).label || ''}`;
      const message = form.company.trim()
        ? `Company / organization: ${form.company.trim()}\n\n${form.message}`
        : form.message;
      await axios.post(
        SITE.mailEndpoint,
        { full_Name: form.full_Name, email: form.email, phone: form.phone, service: topicLabel, message, website: form.website },
        { headers: { 'Content-Type': 'application/json;charset=utf-8' } }
      );
      toast.success(c.form.success, { duration: 3500 });
      setForm({ ...EMPTY, service: topic });
    } catch (err) {
      toast.error(c.form.error, { duration: 4000 });
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: 'fa-solid fa-location-dot', label: c.info.address, value: SITE.address[lang] },
    { icon: 'fa-solid fa-phone', label: c.info.phone, value: SITE.phoneDisplay, href: `tel:${SITE.phoneIntl}`, ltr: true },
    SITE.whatsapp && { icon: 'fa-brands fa-whatsapp', label: c.info.whatsapp, value: c.info.whatsappCta, href: SITE.whatsapp, external: true },
    { icon: 'fa-solid fa-envelope', label: c.info.email, value: SITE.email, href: `mailto:${SITE.email}` },
  ].filter(Boolean);

  return (
    <>
      <PageHeader crumb={c.pageTitle} eyebrow={c.eyebrow} title={c.title} lead={c.lead} />

      <section className="section">
        <div className="container contact">
          <Reveal className="contact__form card">
            <form onSubmit={onSubmit} noValidate>
              <div className="hp-field" aria-hidden="true">
                <label>
                  Website
                  <input name="website" value={form.website} onChange={onChange} tabIndex={-1} autoComplete="off" />
                </label>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>{c.form.name} *</span>
                  <input name="full_Name" value={form.full_Name} onChange={onChange} autoComplete="name" required />
                </label>
                <label className="field">
                  <span>{c.form.email} *</span>
                  <input type="email" name="email" value={form.email} onChange={onChange} autoComplete="email" dir="ltr" required />
                </label>
                <label className="field">
                  <span>{c.form.phone}</span>
                  <input type="tel" name="phone" value={form.phone} onChange={onChange} autoComplete="tel" dir="ltr" />
                </label>
                <label className="field">
                  <span>{c.form.company}</span>
                  <input name="company" value={form.company} onChange={onChange} autoComplete="organization" />
                </label>
                <label className="field field--full">
                  <span>{c.form.topic}</span>
                  <select name="service" value={form.service} onChange={onChange}>
                    <option value="">—</option>
                    {c.form.topics.map((tp) => (
                      <option key={tp.key} value={tp.key}>
                        {tp.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field field--full">
                  <span>{c.form.message} *</span>
                  <textarea name="message" rows="6" value={form.message} onChange={onChange} required />
                </label>
              </div>
              <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={sending}>
                {sending ? c.form.sending : c.form.send}
                {!sending && <i className="fa-solid fa-paper-plane flip-rtl" aria-hidden="true" />}
              </button>
            </form>
          </Reveal>

          <Reveal delay={120} className="contact__info">
            {info.map((it) => (
              <div key={it.label} className="info-row">
                <span className="icon-tile icon-tile--soft">
                  <i className={it.icon} aria-hidden="true" />
                </span>
                <div>
                  <h3>{it.label}</h3>
                  {it.href ? (
                    <a
                      href={it.href}
                      dir={it.ltr ? 'ltr' : undefined}
                      {...(it.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {it.value}
                    </a>
                  ) : (
                    <p>{it.value}</p>
                  )}
                </div>
              </div>
            ))}
          </Reveal>
        </div>

        <div className="container">
          <Reveal className="map">
            <iframe
              title="Safe Hand Apps location"
              src={SITE.mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
